import { x25519 } from "@noble/curves/ed25519";
import { hkdf } from "@noble/hashes/hkdf";
import { sha256 } from "@noble/hashes/sha2";
import { randomBytes } from "@noble/ciphers/webcrypto";

/**
 * Pair-key agreement.
 *
 * Each device generates a fresh X25519 keypair on install. To bond with
 * a friend, both publish their X25519 public keys (over QR / link).
 * Each side derives the shared point and runs HKDF-SHA256 to produce
 * a 32-byte symmetric pair-key used for all ChaCha20-Poly1305 envelopes
 * exchanged through the relay.
 *
 * The relay never sees the pair-key — only opaque encrypted blobs.
 *
 * # Pair ID
 * To index encrypted blobs in D1 without learning identities, we hash the
 * shared symmetric key with a fixed salt and truncate. The same pair-id is
 * derivable on both sides. The server cannot reverse it.
 */

export interface KeyPair {
  /** 32-byte private scalar — never leaves the device. */
  privateKey: Uint8Array;
  /** 32-byte X25519 public point — safe to publish. */
  publicKey: Uint8Array;
}

const HKDF_INFO_PAIR = new TextEncoder().encode("vutwin/pair-key/v1");
const HKDF_INFO_PAIR_ID = new TextEncoder().encode("vutwin/pair-id/v1");
const HKDF_SALT = new TextEncoder().encode("vutwin");

/** Generate a fresh device keypair. Persist privateKey in IndexedDB. */
export function generateKeyPair(): KeyPair {
  const privateKey = randomBytes(32);
  const publicKey = x25519.getPublicKey(privateKey);
  return { privateKey, publicKey };
}

/** Derive the symmetric pair-key from your private key + their public key. */
export function derivePairKey(
  myPrivate: Uint8Array,
  theirPublic: Uint8Array,
): Uint8Array {
  if (myPrivate.length !== 32)
    throw new Error("derivePairKey: private key must be 32 bytes");
  if (theirPublic.length !== 32)
    throw new Error("derivePairKey: public key must be 32 bytes");
  const shared = x25519.getSharedSecret(myPrivate, theirPublic);
  return hkdf(sha256, shared, HKDF_SALT, HKDF_INFO_PAIR, 32);
}

/** Derive a server-routable pair ID. Same on both sides. Server can't reverse. */
export function derivePairId(pairKey: Uint8Array): string {
  const id = hkdf(sha256, pairKey, HKDF_SALT, HKDF_INFO_PAIR_ID, 16);
  // Hex is plenty for an opaque routing id.
  return Array.from(id, (b) => b.toString(16).padStart(2, "0")).join("");
}

const HKDF_INFO_INBOX = new TextEncoder().encode("vutwin/inbox-id/v1");

/**
 * Derive an opaque "inbox id" for a public key.
 *
 * Used by the bond-handshake endpoint so the server can route "B says hi"
 * notifications without learning the recipient's pubkey unless they poll.
 * Same algorithm as `derivePairId` (HKDF over a static salt) so we get
 * a 32-hex-char opaque identifier with no collisions in practice.
 */
export function deriveInboxId(publicKey: Uint8Array): string {
  if (publicKey.length !== 32)
    throw new Error("deriveInboxId: pubkey must be 32 bytes");
  const id = hkdf(sha256, publicKey, HKDF_SALT, HKDF_INFO_INBOX, 16);
  return Array.from(id, (b) => b.toString(16).padStart(2, "0")).join("");
}
